// /docstudio — 통합 문서 작성.
// 한글·워드·파워포인트·포토샵·일러스트·프리미어 파일을 한 화면에서 칸별로 보고,
// 올리면 확장자로 알맞은 칸에 들어간다. '열기' 는
//   · TIM_BOX 데스크톱 앱: 실제 프로그램(Hwp.exe, WINWORD.EXE …)으로 열고, 저장하면 자동 되올림
//   · 웹/안드로이드: 내려받기 → OS 가 기본 앱으로 연다
// ArtCraft 칸은 서버가 자동으로 받아 둔 최신 설치본을 내려주고, 데스크톱 앱에서는 설치·실행·자동 업데이트까지 한다.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import { useAuth } from "../../shared/stores/auth";
import { toast } from "../../shared/stores/toasts";
import { I } from "../../shared/Icon";
import { docsBase, viewPath } from "./base";
import { chunkedUpload, fileSource, nativeSource, fmtSpeed, fmtEta, type UploadSource, type UploadProgress } from "./chunkUpload";
import { PageHeader } from "../../shared/components/PageHeader";
import { APP_INSTALL_URL, isInsideTimboxApp } from "../../shared/nativeApp";

type AppDef = { id: string; label: string; program: string; color: string; exts: string[] };
type DocFile = { id: string; name: string; ext: string; app: string; size: number; rev: number; created_at: string; updated_at: string };
type Asset = { name: string; size: number; os: string; label: string; hint: string; primary: boolean; ready: boolean };
type ArtState = {
  version: string; tag: string; published_at: string | null; release_url: string; assets: Asset[];
  last_checked: string | null; last_synced: string | null; last_error: string | null;
  syncing: boolean; progress: Record<string, { done: number; total: number }>; auto_sync: boolean; sync_hours: number;
};
type Summary = {
  total_files: number; total_size: number; by_app: Record<string, { count: number; size: number }>;
  edited_files: number; edited_today: number; versions: { count: number; size: number };
  recent: DocFile[]; last_activity: string | null; storage: string;
};
type Detected = Record<string, { installed: boolean; path?: string }>;
type ArtLocal = { installed: boolean; version?: string; path?: string };

// 데스크톱 앱(Electron preload)이 붙여 주는 다리. 웹·안드로이드에서는 없다.
type DocsBridge = {
  detectApps: () => Promise<Detected>;
  openFile: (o: { id: string; name: string; app: string; rev: number; downloadUrl: string; token: string }) =>
    Promise<{ ok: boolean; via?: "program" | "default"; program?: string; path?: string; error?: string }>;
  onFileSaved: (cb: (e: { id: string }) => void) => () => void;
  uploadBack: (o: { id: string; uploadUrl: string; token: string }) => Promise<{ ok: boolean; status?: number; error?: string }>;
  artcraftStatus: () => Promise<ArtLocal>;
  artcraftLaunch: () => Promise<{ ok: boolean; error?: string }>;
  artcraftInstall: (o: { url: string; token: string; version: string; silent: boolean }) => Promise<{ ok: boolean; error?: string }>;
};
const bridge = (): DocsBridge | undefined => (window as unknown as { timbox?: { docs?: DocsBridge } }).timbox?.docs;

const PRETENDARD = "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, system-ui, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif";
const BADGE: Record<string, string> = { hwp: "한", word: "W", excel: "X", ppt: "P", photoshop: "Ps", illustrator: "Ai", premiere: "Pr", pdf: "PDF", artcraft: "AC" };
const ART_COLOR = "#7C3AED";

// 휴대폰 화면(좁은 폭) 전용 배치 — 칸은 옆으로 넘기는 한 줄, 파일 줄은 이름 아래 큰 단추,
// 긴 안내는 숨기고 글자·단추를 키운다. (앱 WebView 에서 손가락으로 쓰기 편하게)
const DS_CSS = `
.ds-root{padding:20px 24px 40px}
@media (max-width:640px){
 .ds-root{padding:12px 12px 32px;font-size:15px}
 .ds-root .t-page-header{flex-direction:column;align-items:stretch;gap:10px;margin-bottom:12px}
 .ds-root .t-page-header h1{font-size:22px}
 .ds-root .t-page-header .subtitle{display:none}
 .ds-root .t-page-header>div:last-child{justify-content:stretch}
 .ds-root .t-page-header>div:last-child>*{flex:1;justify-content:center}
 .ds-root .t-btn{min-height:40px;font-size:14px}
 .ds-notice{display:none!important}
 .ds-tabs{display:flex!important;overflow-x:auto;gap:8px!important;-webkit-overflow-scrolling:touch;scroll-snap-type:x proximity;margin:0 -12px 12px!important;padding:0 12px 6px}
 .ds-tabs>button{flex:0 0 auto;min-width:118px;scroll-snap-align:start;padding:10px!important}
 .ds-row{flex-wrap:wrap;padding:12px!important;gap:10px!important}
 .ds-row .ds-main{flex:1 1 calc(100% - 46px)!important}
 .ds-row .ds-main>div:first-child{white-space:normal!important;word-break:break-all;font-size:15px!important}
 .ds-actions{width:100%;display:flex!important;gap:8px}
 .ds-actions .ds-open{flex:1;justify-content:center}
 .ds-listhead{flex-wrap:wrap}
 .ds-listhead input{width:100%!important;order:3;min-height:40px;font-size:15px!important}
 .ds-kpis{grid-template-columns:1fr 1fr!important}
 .ds-two{grid-template-columns:1fr!important}
 .ds-drop{padding:16px 12px!important}
 .ds-hide-m{display:none!important}
}`;

function fmtBytes(n: number) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}
function fmtTime(iso: string | null | undefined) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function cmpVer(a = "", b = "") {
  const pa = a.split(".").map(Number), pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d;
  }
  return 0;
}
// 받침에 따라 '로' / '으로' — "포토샵으로", "한글로"(ㄹ 받침은 '로').
function ro(w: string) {
  const c = w.charCodeAt(w.length - 1) - 0xac00;
  if (c < 0 || c > 11171) return `${w}(으)로`;
  const jong = c % 28;
  return w + (jong === 0 || jong === 8 ? "로" : "으로");
}
function errMsg(e: any) { return e?.response?.data?.detail ?? e?.message ?? "요청에 실패했습니다"; }
const absUrl = (p: string) => `${window.location.origin}/api/v1${p}`;
const userOS = /Mac|iPhone|iPad/i.test(navigator.userAgent) ? "mac" : /Android/i.test(navigator.userAgent) ? "android" : "windows";

function AppBadge({ id, color, size = 34 }: { id: string; color: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.26, background: color, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800,
      fontSize: size * (BADGE[id]?.length > 1 ? 0.38 : 0.46), letterSpacing: "-0.02em", flexShrink: 0,
    }}>{BADGE[id] ?? "?"}</div>
  );
}

export default function DocStudioPage() {
  const [apps, setApps] = useState<AppDef[]>([]);
  const [files, setFiles] = useState<DocFile[]>([]);
  const [tab, setTab] = useState<string>("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [maxMb, setMaxMb] = useState(2048);
  const [detected, setDetected] = useState<Detected | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [art, setArt] = useState<ArtState | null>(null);
  const [artLocal, setArtLocal] = useState<ArtLocal | null>(null);
  const [artBusy, setArtBusy] = useState(false);
  const [autoUpdate, setAutoUpdate] = useState(() => { try { return localStorage.getItem("docstudio.artcraftAuto") !== "0"; } catch { return true; } });
  const input = useRef<HTMLInputElement>(null);
  const autoTried = useRef<string>("");
  const desk = bridge();
  const nav = useNavigate();

  const appById = useMemo(() => Object.fromEntries(apps.map(a => [a.id, a])), [apps]);

  const [summary, setSummary] = useState<Summary | null>(null);

  // ── 휴대폰 오프라인 보관(보기 전용) — 안드로이드 앱에서만 ──
  type OffRec = { id: string; rev: number; name: string; kind: string; pages?: number; saved_at?: string };
  const offline = (window as unknown as { TimboxOffline?: { list: () => string; save: (m: string) => void; remove: (id: string) => void; open: () => void } }).TimboxOffline;
  const [offRecs, setOffRecs] = useState<Record<string, OffRec>>({});
  const [offBusy, setOffBusy] = useState<Record<string, boolean>>({});
  const [offlineExts, setOfflineExts] = useState<Set<string>>(new Set());
  const readOffline = useCallback(() => {
    if (!offline) return;
    try { setOffRecs(Object.fromEntries((JSON.parse(offline.list()) as OffRec[]).map(r => [r.id, r]))); } catch { /* 비어 있음 */ }
  }, [offline]);
  useEffect(() => {
    if (!offline) return;
    readOffline();
    api.get("/docstudio/offline-kinds").then(r => setOfflineExts(new Set(r.data.exts))).catch(() => {});
    (window as unknown as { __timboxOffline?: (e: { id: string; ok: boolean; error?: string; record?: OffRec }) => void }).__timboxOffline = e => {
      setOffBusy(b => ({ ...b, [e.id]: false }));
      readOffline();
      if (e.ok) toast.success("휴대폰에 보관했습니다", `${e.record?.name ?? ""} — 인터넷 없이 '오프라인 문서함'에서 볼 수 있습니다`);
      else toast.error("보관하지 못했습니다", e.error);
    };
  }, [offline, readOffline]);

  const saveOffline = useCallback(async (f: DocFile) => {
    if (!offline) return;
    setOffBusy(b => ({ ...b, [f.id]: true }));
    try {
      const d = (await api.get(`/docstudio/files/${f.id}/offline`, { timeout: 0 })).data;
      offline.save(JSON.stringify(d));
    } catch (e) { setOffBusy(b => ({ ...b, [f.id]: false })); toast.error("보관하지 못했습니다", errMsg(e)); }
  }, [offline]);

  const toggleOffline = (f: DocFile) => {
    const rec = offRecs[f.id];
    if (rec && rec.rev >= f.rev) {
      if (!window.confirm(`'${f.name}' 을(를) 휴대폰 보관함에서 뺄까요?`)) return;
      offline?.remove(f.id); readOffline(); toast.info("보관을 해제했습니다");
      return;
    }
    saveOffline(f);
  };

  // 보관한 문서가 서버에서 새 판이 되면(다른 기기에서 고침) 인터넷이 될 때 알아서 새로 받아 둔다
  const autoSynced = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!offline) return;
    for (const f of files) {
      const rec = offRecs[f.id];
      const key = `${f.id}:${f.rev}`;
      if (rec && rec.rev < f.rev && !offBusy[f.id] && !autoSynced.current.has(key)) {
        autoSynced.current.add(key);
        saveOffline(f);
      }
    }
  }, [files, offRecs, offBusy, offline, saveOffline]);

  // ── 앱 받기(공개 설치본 목록) ──
  type Rel = { kind: string; version: string; ready: boolean; size: number };
  const [rels, setRels] = useState<Rel[]>([]);
  useEffect(() => { api.get<{ items: Rel[] }>("/app/downloads").then(r => setRels(r.data.items)).catch(() => {}); }, []);
  const [newFor, setNewFor] = useState<string | null>(null); // "pick" = 종류부터 고르기, 아니면 칸 id
  const [newFormats, setNewFormats] = useState<Record<string, { ext: string; label: string }[]>>({});
  useEffect(() => { api.get("/docstudio/new-formats").then(r => setNewFormats(r.data.formats)).catch(() => {}); }, []);
  const loadFiles = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([
        api.get<{ items: DocFile[] }>("/docstudio/files"),
        api.get<Summary>("/docstudio/summary"),
      ]);
      setFiles(f.data.items); setSummary(s.data);
    } catch (e) { toast.error("파일 목록을 불러오지 못했습니다", errMsg(e)); }
  }, []);

  // 다른 기기(휴대폰·다른 PC)에서 고친 내용도 보이도록 30초마다 서버 목록을 다시 받는다.
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") loadFiles(); }, 30_000);
    return () => clearInterval(t);
  }, [loadFiles]);

  const loadArt = useCallback(async () => {
    try { setArt((await api.get<ArtState>("/docstudio/artcraft")).data); } catch { /* 다음 주기에 다시 */ }
  }, []);

  useEffect(() => {
    api.get<{ apps: AppDef[]; max_mb: number }>("/docstudio/apps").then(r => { setApps(r.data.apps); setMaxMb(r.data.max_mb); }).catch(() => {});
    loadFiles();
    loadArt();
    desk?.detectApps().then(setDetected).catch(() => {});
    desk?.artcraftStatus().then(setArtLocal).catch(() => {});
    const onFocus = () => loadFiles();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 다른 앱에서 "TIM_BOX 로 열기/공유" 한 파일 — 안드로이드 앱이 넘겨준 것을 서버에 올리고 편집기로 연다.
  const imported = useRef(false);
  useEffect(() => {
    const nat = (window as unknown as { TimboxNative?: { pendingFiles: () => string; clearPending: () => void } }).TimboxNative;
    if (!nat || imported.current || !apps.length) return;
    let list: { name: string; mime: string; url: string; size: number }[] = [];
    try { list = JSON.parse(nat.pendingFiles() || "[]"); } catch { list = []; }
    if (!list.length) return;
    imported.current = true;
    (async () => {
      try {
        // 통째로 메모리에 올리지 않고, 앱이 조각씩 읽어 주는 대로 올린다
        await upload(list.map(x => nativeSource(x)));
        nat.clearPending();
      } catch (e) { toast.error("가져오기 실패", errMsg(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apps]);

  // 업데이트 자동 반영 — 받는 중이면 3초, 평소엔 5분마다 서버의 최신 버전을 다시 본다.
  useEffect(() => {
    const t = setTimeout(loadArt, art?.syncing ? 3000 : 5 * 60 * 1000);
    return () => clearTimeout(t);
  }, [art, loadArt]);

  // 프로그램에서 저장하면 데스크톱 앱이 알려 준다 → 팀박스로 되올린다.
  useEffect(() => {
    if (!desk) return;
    return desk.onFileSaved(async ({ id }) => {
      try {
        await api.get("/docstudio/apps"); // 토큰이 만료됐으면 여기서 갱신된다
        const token = useAuth.getState().accessToken ?? "";
        const r = await desk.uploadBack({ id, uploadUrl: absUrl(`/docstudio/files/${id}/content`), token });
        if (r.ok) { toast.success("저장한 내용이 팀박스에 반영됐습니다"); loadFiles(); }
        else toast.error("팀박스로 되올리지 못했습니다", r.error);
      } catch (e) { toast.error("팀박스로 되올리지 못했습니다", errMsg(e)); }
    });
  }, [desk, loadFiles]);

  // ── 올리기: 8 MB 조각 · 진행률 · 끊기면 이어 올리기 ──
  type UpRow = { key: string; name: string; p: UploadProgress | null; state: "wait" | "up" | "done" | "fail"; err?: string };
  const [ups, setUps] = useState<UpRow[]>([]);
  const upAbort = useRef<AbortController | null>(null);
  const native = () => (window as unknown as { TimboxNative?: { uploadProgress?: (n: string, pct: number, text: string) => void; uploadDone?: (n: string, path: string) => void; uploadFailed?: (n: string, why: string) => void } }).TimboxNative;

  const upload = async (list: FileList | File[] | UploadSource[]) => {
    const arr: UploadSource[] = Array.from(list as ArrayLike<File | UploadSource>).map(x => (x instanceof File ? fileSource(x) : x));
    if (!arr.length) return;
    const tooBig = arr.find(f => f.size > maxMb * 1024 * 1024);
    if (tooBig) { toast.error("파일이 너무 큽니다", `${tooBig.name} (최대 ${maxMb} MB)`); return; }
    const app = tab !== "all" && tab !== "artcraft" ? tab : undefined;
    const ctl = new AbortController();
    upAbort.current = ctl;
    setUps(arr.map(f => ({ key: f.key, name: f.name, p: null, state: "wait" })));
    setBusy(true);
    const added: DocFile[] = [];
    let lastNote = 0;
    for (const src of arr) {
      setUps(u => u.map(r => (r.key === src.key ? { ...r, state: "up" } : r)));
      try {
        const item = await chunkedUpload<DocFile>(src, {
          app, signal: ctl.signal,
          onProgress: p => {
            setUps(u => u.map(r => (r.key === src.key ? { ...r, p } : r)));
            // 휴대폰 앱이면 알림줄에도 진행률을 띄운다(다른 앱을 보고 있어도 보이게) — 1초에 한 번
            const now = Date.now();
            if (now - lastNote > 1000) {
              lastNote = now;
              const pct = Math.floor((p.loaded / Math.max(1, p.total)) * 100);
              native()?.uploadProgress?.(src.name, pct, p.state === "retrying" ? `연결 끊김 — ${p.retryIn}초 뒤 이어 올림` : `${fmtSpeed(p.speed)} · 남은 시간 ${fmtEta(p.eta)}`);
            }
          },
        });
        added.push(item);
        setUps(u => u.map(r => (r.key === src.key ? { ...r, state: "done" } : r)));
        native()?.uploadDone?.(item.name, viewPath(item.id));
      } catch (e: any) {
        if (ctl.signal.aborted) break;
        const why = errMsg(e);
        setUps(u => u.map(r => (r.key === src.key ? { ...r, state: "fail", err: why } : r)));
        native()?.uploadFailed?.(src.name, why);
        toast.warn(`${src.name} — 올리지 못함`, why);
      }
    }
    setBusy(false);
    upAbort.current = null;
    if (input.current) input.current.value = "";
    if (added.length) {
      const where = [...new Set(added.map(a => appById[a.app]?.label ?? a.app))].join(", ");
      toast.success(`${added.length}개 파일을 올렸습니다`, `${where} 칸으로 자동 분류`);
      if (added.length === 1 && tab === "all") setTab(added[0].app);
      await loadFiles();
      // 한 개만 올렸으면 바로 웹 편집기로 연다(앱·브라우저 어디서나)
      if (added.length === 1) nav(viewPath(added[0].id));
    }
    setTimeout(() => setUps(u => u.filter(r => r.state === "fail")), 4000);
  };

  const cancelUploads = async () => {
    upAbort.current?.abort();
    toast.info("올리기를 멈췄습니다", "같은 파일을 다시 올리면 멈춘 곳부터 이어 올립니다");
  };

  const fetchBlob = async (f: DocFile) =>
    (await api.get(`/docstudio/files/${f.id}/download`, { responseType: "blob", timeout: 0 })).data as Blob;

  const download = async (f: DocFile) => {
    // 안드로이드 앱(WebView)은 blob: 주소를 못 받는다 → 서명 주소를 다운로드 관리자에 넘긴다
    if (isInsideTimboxApp() && !desk) {
      try {
        const o = (await api.get<{ raw_url: string }>(`/docstudio/files/${f.id}/open`)).data;
        window.location.href = `${o.raw_url}&dl=1`;
      } catch (e) { toast.error("내려받기 실패", errMsg(e)); }
      return;
    }
    try {
      const url = URL.createObjectURL(await fetchBlob(f));
      const a = document.createElement("a");
      a.href = url; a.download = f.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error("내려받기 실패", errMsg(e)); }
  };

  const openFile = async (f: DocFile) => {
    const app = appById[f.app];
    if (!desk) {
      // 브라우저는 PC 프로그램을 직접 실행할 수 없다 → 내려받으면 OS 가 기본 앱으로 연다
      await download(f);
      toast.info(`${f.name} 내려받기`, `내려받은 파일을 열면 ${ro(app?.label ?? "기본 프로그램")} 열립니다`);
      return;
    }
    setOpening(f.id);
    try {
      await api.get("/docstudio/apps");
      const token = useAuth.getState().accessToken ?? "";
      const r = await desk.openFile({ id: f.id, name: f.name, app: f.app, rev: f.rev, downloadUrl: absUrl(`/docstudio/files/${f.id}/download`), token });
      if (!r.ok) toast.error("열지 못했습니다", r.error);
      else if (r.via === "program") toast.success(`${ro(app?.label ?? "프로그램")} 열었습니다`, "저장하면 팀박스에 자동 반영됩니다");
      else toast.info(`이 PC에 ${app?.label ?? "해당"} 프로그램이 없어 기본 프로그램으로 열었습니다`, "저장하면 팀박스에 자동 반영됩니다");
    } catch (e) { toast.error("열지 못했습니다", errMsg(e)); }
    finally { setOpening(null); }
  };



  const remove = async (f: DocFile) => {
    if (!window.confirm(`'${f.name}' 을(를) 삭제할까요?`)) return;
    try { await api.delete(`/docstudio/files/${f.id}`); toast.success("삭제했습니다"); loadFiles(); }
    catch (e) { toast.error("삭제 실패", errMsg(e)); }
  };

  // ── ArtCraft ──
  const artSync = async () => {
    setArtBusy(true);
    try { setArt((await api.post<ArtState>("/docstudio/artcraft/sync")).data); toast.info("최신 버전을 확인하고 있습니다"); }
    catch (e) { toast.error("확인 실패", errMsg(e)); }
    finally { setArtBusy(false); }
  };

  const artDownload = async (a: Asset) => {
    try {
      const r = await api.get(`/docstudio/artcraft/download/${encodeURIComponent(a.name)}`, { responseType: "blob", timeout: 0 });
      const url = URL.createObjectURL(r.data as Blob);
      const el = document.createElement("a");
      el.href = url; el.download = a.name; el.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error("내려받기 실패", errMsg(e)); }
  };

  const winSetup = art?.assets.find(a => a.os === "windows" && a.primary && a.ready);
  const needsUpdate = !!(artLocal?.installed && art?.version && cmpVer(art.version, artLocal.version) > 0);

  const artInstall = useCallback(async (silent: boolean) => {
    if (!desk || !winSetup || !art) return;
    setArtBusy(true);
    try {
      await api.get("/docstudio/apps");
      const token = useAuth.getState().accessToken ?? "";
      const r = await desk.artcraftInstall({ url: absUrl(`/docstudio/artcraft/download/${encodeURIComponent(winSetup.name)}`), token, version: art.version, silent });
      if (r.ok) { toast.success(`ArtCraft ${art.version} 설치를 마쳤습니다`); setArtLocal(await desk.artcraftStatus()); }
      else toast.error("ArtCraft 설치 실패", r.error);
    } catch (e) { toast.error("ArtCraft 설치 실패", errMsg(e)); }
    finally { setArtBusy(false); }
  }, [desk, winSetup, art]);

  // 데스크톱 앱 + 자동 업데이트 켜짐 + 설치된 버전이 옛것 → 알아서 새 버전으로 바꾼다(버전당 한 번만 시도).
  useEffect(() => {
    if (!autoUpdate || !needsUpdate || !art || autoTried.current === art.version) return;
    autoTried.current = art.version;
    toast.info(`ArtCraft ${artLocal?.version} → ${art.version} 자동 업데이트를 시작합니다`);
    artInstall(true);
  }, [autoUpdate, needsUpdate, art, artLocal, artInstall]);

  const toggleAuto = () => {
    const v = !autoUpdate;
    setAutoUpdate(v);
    try { localStorage.setItem("docstudio.artcraftAuto", v ? "1" : "0"); } catch { /* noop */ }
  };

  const shown = files.filter(f => (tab === "all" || f.app === tab) && (!q || f.name.toLowerCase().includes(q.toLowerCase())));
  const count = (id: string) => files.filter(f => f.app === id).length;
  const curApp = appById[tab];

  const tabs = [
    { id: "all", label: "전체", color: "#475569", sub: `${files.length}개` },
    ...apps.map(a => ({ id: a.id, label: a.label, color: a.color, sub: `${count(a.id)}개` })),
    { id: "artcraft", label: "ArtCraft", color: ART_COLOR, sub: art?.version ? `v${art.version}` : "AI 이미지·영상" },
  ];

  return (
    <div data-testid="docstudio" className="ds-root" style={{ fontFamily: PRETENDARD, width: "100%", maxWidth: 1280, margin: "0 auto", minWidth: 0 }}>
      <style>{DS_CSS}</style>
      <PageHeader
        title={docsBase() === "/teamdocs" ? "내 문서" : "통합 문서 작성"}
        subtitle="한글 · 워드 · 엑셀 · 파워포인트 · PDF · 포토샵 · 일러스트 · 영상을 한곳에서. 올리면 알맞은 편집기로 자동 연결되고, 웹·휴대폰 앱에서 바로 보고 고칩니다."
        right={<>
          <button className="t-btn t-btn-primary t-btn-sm" data-action="new-doc" onClick={() => setNewFor(newFormats[tab] ? tab : "pick")} style={{ whiteSpace: "nowrap" }}>
            <I.Plus size={14}/> 새 문서
          </button>
          {offline && <button className="t-btn t-btn-sm" data-action="offline-box" onClick={() => offline.open()} style={{ whiteSpace: "nowrap" }}>오프라인 문서함</button>}
          {!isInsideTimboxApp() && <a className="t-btn t-btn-outline t-btn-sm" href={APP_INSTALL_URL} target="_blank" rel="noreferrer" style={{ whiteSpace: "nowrap" }}>앱 받기</a>}
        </>}
      />

      {!desk && (
        <div className="t-card ds-notice" style={{ padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "var(--text-2)", display: "flex", flexDirection: "row", gap: 8, alignItems: "center" }}>
          <I.AlertC size={16}/>
          '미리보기' 는 보기 전용으로 빨리 열리고, '편집' 은 프로그램 설치 없이 이 화면(웹·휴대폰 앱)에서 바로 고칩니다.
          TIM_BOX 데스크톱 앱에서는 PC 에 설치된 한글·오피스·어도비 프로그램으로 여는 버튼도 함께 나옵니다.
        </div>
      )}

      {/* 프로그램 칸 */}
      <div role="tablist" className="ds-tabs" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))", gap: 10, marginBottom: 16 }}>
        {tabs.map(t => {
          const on = tab === t.id;
          const det = detected?.[t.id];
          return (
            <button key={t.id} role="tab" aria-selected={on} data-app={t.id} onClick={() => setTab(t.id)}
              className="t-card"
              style={{
                display: "flex", flexDirection: "row", alignItems: "center", gap: 10, padding: "12px 12px", cursor: "pointer", textAlign: "left",
                border: on ? `2px solid ${t.color}` : "1px solid var(--border)", background: on ? "var(--p-tint)" : "var(--surface)",
                fontFamily: "inherit", color: "var(--text)",
              }}>
              {t.id === "all"
                ? <div style={{ width: 34, height: 34, borderRadius: 9, background: t.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><I.Docs size={18}/></div>
                : <AppBadge id={t.id} color={t.color}/>}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{t.label}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                  {t.sub}
                  {det && t.id !== "all" && t.id !== "artcraft" && (
                    <span style={{ marginLeft: 6, color: det.installed ? "var(--ok)" : "var(--text-3)" }}>· {det.installed ? "설치됨" : "미설치"}</span>
                  )}
                  {t.id === "artcraft" && artLocal && (
                    <span style={{ marginLeft: 6, color: artLocal.installed ? "var(--ok)" : "var(--text-3)" }}>· {artLocal.installed ? "설치됨" : "미설치"}</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {tab === "artcraft" ? (
        <ArtCraftPanel
          art={art} artLocal={artLocal} desk={!!desk} busy={artBusy} autoUpdate={autoUpdate} needsUpdate={needsUpdate}
          onSync={artSync} onDownload={artDownload} onToggleAuto={toggleAuto}
          onInstall={() => artInstall(false)}
          onLaunch={async () => { const r = await desk?.artcraftLaunch(); if (r && !r.ok) toast.error("ArtCraft 실행 실패", r.error); }}
        />
      ) : (
        <>
          {tab === "all" && summary && (
            <Dashboard summary={summary} apps={apps} art={art} desk={!!desk} rels={rels} offlineCount={offline ? Object.keys(offRecs).length : null}
              onPick={id => setTab(id)} onOpen={id => nav(viewPath(id, "view"))}/>
          )}

          {curApp && newFormats[curApp.id] && (
            <button className="t-btn" data-action="new-in-tab" onClick={() => setNewFor(curApp.id)}
              style={{ width: "100%", justifyContent: "center", marginBottom: 10, borderStyle: "dashed", borderColor: curApp.color, color: curApp.color, fontWeight: 700 }}>
              <I.Plus size={15}/> 새 {curApp.label} {curApp.id === "photoshop" || curApp.id === "illustrator" ? "그림" : "문서"} 만들기
            </button>
          )}

          {ups.length > 0 && (
            <div className="t-card" data-testid="upload-panel" style={{ display: "block", padding: 12, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, flex: 1 }}>올리는 중 {ups.filter(u => u.state === "done").length}/{ups.length}</div>
                {busy && <button className="t-btn t-btn-sm" data-action="cancel-upload" onClick={cancelUploads}>멈추기</button>}
              </div>
              {ups.map(u => {
                const pct = u.p ? Math.floor((u.p.loaded / Math.max(1, u.p.total)) * 100) : u.state === "done" ? 100 : 0;
                return (
                  <div key={u.key} data-upload={u.name} style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", gap: 8, fontSize: 12.5, marginBottom: 3 }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{u.name}</span>
                      <span data-pct style={{ color: u.state === "fail" ? "var(--danger)" : "var(--text-2)", whiteSpace: "nowrap" }}>
                        {u.state === "fail" ? `실패: ${u.err}` : u.state === "done" ? "완료" : u.state === "wait" ? "대기" :
                          u.p?.state === "retrying" ? `연결 끊김 — ${u.p.retryIn}초 뒤 이어 올림` :
                          u.p?.state === "finishing" ? "마무리 중…" :
                          `${pct}% · ${fmtBytes(u.p?.loaded ?? 0)}/${fmtBytes(u.p?.total ?? 0)} · ${fmtSpeed(u.p?.speed ?? 0)} · 남은 ${fmtEta(u.p?.eta ?? null)}`}
                      </span>
                    </div>
                    <div style={{ height: 6, background: "var(--bg-1)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: u.state === "fail" ? "var(--danger)" : u.p?.state === "retrying" ? "var(--warn)" : "var(--p-600)", transition: "width .3s" }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 올리기 */}
          <div
            data-testid="dropzone" className="ds-drop"
            onClick={() => input.current?.click()}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
            style={{
              border: `2px dashed ${drag ? (curApp?.color ?? "var(--p-600)") : "var(--border-strong)"}`, borderRadius: 14,
              padding: "22px 16px", textAlign: "center", cursor: "pointer", marginBottom: 16,
              background: drag ? "var(--p-tint)" : "var(--bg-1)", transition: "all .12s",
            }}>
            <input ref={input} type="file" multiple hidden data-testid="file-input"
              accept={(curApp ? curApp.exts : apps.flatMap(a => a.exts)).map(e => "." + e).join(",")}
              onChange={e => e.target.files && upload(e.target.files)}/>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 6, color: curApp?.color ?? "var(--p-600)" }}><I.Upload size={26}/></div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {busy ? "올리는 중…" : curApp ? `${curApp.label} 파일을 끌어다 놓거나 눌러서 선택` : "파일을 끌어다 놓거나 눌러서 선택 — 자동으로 프로그램별 분류"}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>
              {curApp ? curApp.exts.map(e => "." + e).join(" ") : "hwp · docx · xlsx · pptx · pdf · psd · ai · 이미지 · 영상 등"} · 최대 {maxMb >= 1024 ? `${maxMb / 1024} GB` : `${maxMb} MB`}
            </div>
          </div>

          {/* 목록 */}
          <div className="t-card" style={{ padding: 0, overflow: "hidden", display: "block" }}>
            <div className="ds-listhead" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ fontWeight: 700 }}>{curApp ? `${curApp.label} 파일` : "전체 파일"}</div>
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>{shown.length}개</div>
              <div style={{ flex: 1 }}/>
              <input className="t-input" placeholder="파일 이름 검색" value={q} onChange={e => setQ(e.target.value)} style={{ width: 200, fontSize: 13 }}/>
            </div>
            {shown.length === 0 ? (
              <div style={{ padding: "36px 16px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
                {files.length === 0 ? "아직 올린 파일이 없습니다. 위에 파일을 끌어다 놓아 보세요." : "이 칸에 해당하는 파일이 없습니다."}
              </div>
            ) : (
              <div data-testid="file-list">
                {shown.map(f => {
                  const app = appById[f.app];
                  return (
                    <div key={f.id} data-file={f.name} className="ds-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
                      <AppBadge id={f.app} color={app?.color ?? "#94A3B8"} size={30}/>
                      <div className="ds-main" style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                        <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                          {app?.program ?? f.app} · {fmtBytes(f.size)} · {fmtTime(f.updated_at)}{f.rev > 1 ? ` · ${f.rev}번째 저장본` : ""}
                        </div>
                      </div>
                      <div className="ds-actions" style={{ display: "contents" }}>
                      <button className="t-btn t-btn-primary t-btn-sm ds-open" data-action="open" onClick={() => nav(viewPath(f.id))}
                        style={{ background: app?.color, borderColor: app?.color }}>
                        <I.Edit size={14}/> 편집
                      </button>
                      <button className="t-btn t-btn-sm ds-open" data-action="preview" onClick={() => nav(viewPath(f.id, "view"))} title="보기 전용 — 빨리 열리고 휴대폰에서도 편하게">
                        <I.Eye size={14}/> 미리보기
                      </button>
                      {offline && offlineExts.has(f.ext) && (() => {
                        const rec = offRecs[f.id];
                        const st = offBusy[f.id] ? "busy" : !rec ? "none" : rec.rev < f.rev ? "stale" : "ok";
                        return (
                          <button className="t-btn t-btn-sm" data-action="offline" onClick={() => toggleOffline(f)} disabled={st === "busy"}
                            title="인터넷 없이 볼 수 있게 이 휴대폰에 보관"
                            style={st === "ok" ? { color: "var(--ok)", borderColor: "var(--ok)" } : st === "stale" ? { color: "var(--warn)", borderColor: "var(--warn)" } : undefined}>
                            {st === "busy" ? "보관 중…" : st === "ok" ? "✓ 보관됨" : st === "stale" ? "새 판 받기" : "보관"}
                          </button>
                        );
                      })()}
                      {desk && (
                        <button className="t-btn t-btn-sm" data-action="open-pc" onClick={() => openFile(f)} disabled={opening === f.id} title="이 PC 에 설치된 프로그램으로 열기">
                          <I.Open size={14}/> {opening === f.id ? "여는 중…" : `${ro(app?.label ?? "프로그램")} 열기`}
                        </button>
                      )}
                      <button className="t-btn t-btn-sm" onClick={() => download(f)} title="내려받기"><I.Download size={14}/></button>
                      <button className="t-btn t-btn-sm t-btn-ghost" onClick={() => remove(f)} title="삭제"><I.Trash size={14}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {newFor && (
        <NewDocDialog initial={newFor} apps={apps} formats={newFormats} onClose={() => setNewFor(null)}
          onCreated={f => { setNewFor(null); toast.success(`${f.name} 을(를) 만들었습니다`); nav(viewPath(f.id)); }}/>
      )}

    </div>
  );
}

// ── 새 문서 만들기 ─────────────────────────────────────────────────────
function NewDocDialog({ initial, apps, formats, onClose, onCreated }: {
  initial: string; apps: AppDef[]; formats: Record<string, { ext: string; label: string }[]>;
  onClose: () => void; onCreated: (f: DocFile) => void;
}) {
  const [app, setApp] = useState<string | null>(initial === "pick" ? null : initial);
  const [ext, setExt] = useState<string>("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const choices = apps.filter(a => formats[a.id]);
  const cur = app ? apps.find(a => a.id === app) : null;
  useEffect(() => { if (app && formats[app]) setExt(formats[app][0].ext); }, [app, formats]);
  const create = async () => {
    if (!app) return;
    setBusy(true);
    try { onCreated((await api.post<DocFile>("/docstudio/new", { app, ext, name: name.trim() || undefined })).data); }
    catch (e) { toast.error("만들지 못했습니다", errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} data-testid="new-doc-dialog" className="t-card"
        style={{ width: "min(520px, 100%)", maxHeight: "90vh", overflow: "auto", padding: 18, display: "block", fontFamily: PRETENDARD }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 17, flex: 1 }}>{cur ? `새 ${cur.label} 만들기` : "새 문서 만들기"}</div>
          <button className="t-btn t-btn-sm t-btn-ghost" onClick={onClose}><I.X size={14}/></button>
        </div>
        {!cur ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
            {choices.map(a => (
              <button key={a.id} data-new-app={a.id} className="t-card" onClick={() => setApp(a.id)}
                style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 10, padding: 12, cursor: "pointer", fontFamily: "inherit", color: "var(--text)", textAlign: "left" }}>
                <AppBadge id={a.id} color={a.color}/>
                <span style={{ fontWeight: 700 }}>{a.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <AppBadge id={cur.id} color={cur.color} size={40}/>
              <div style={{ fontSize: 13, color: "var(--text-2)" }}>빈 {cur.label} 파일을 만들어 바로 편집기로 엽니다. 서버의 내 폴더에 저장됩니다.</div>
            </div>
            {formats[cur.id].length > 1 && (
              <div style={{ display: "grid", gap: 6 }}>
                {formats[cur.id].map(f => (
                  <label key={f.ext} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, cursor: "pointer" }}>
                    <input type="radio" name="newext" checked={ext === f.ext} onChange={() => setExt(f.ext)}/> {f.label}
                  </label>
                ))}
              </div>
            )}
            <label style={{ display: "grid", gap: 4, fontSize: 13 }}>
              파일 이름
              <input className="t-input" data-testid="new-doc-name" placeholder={`예: 회의록 (비워 두면 '새 ${cur.label} 문서')`} value={name}
                onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") create(); }} autoFocus/>
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {initial === "pick" && <button className="t-btn" onClick={() => setApp(null)}>다른 종류</button>}
              <button className="t-btn t-btn-primary" data-action="create" onClick={create} disabled={busy || !ext}
                style={{ background: cur.color, borderColor: cur.color }}>{busy ? "만드는 중…" : "만들고 열기"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 통합 대시보드: 내 문서 현황(아이디별) ─────────────────────────────────
const EDITOR_OF: Record<string, string> = {
  hwp: "rhwp 한글 편집기", word: "ONLYOFFICE 문서", excel: "ONLYOFFICE 스프레드시트", ppt: "ONLYOFFICE 프레젠테이션",
  photoshop: "miniPaint + PSD 레이어", illustrator: "SVG-Edit 벡터", premiere: "재생 + 자르기·변환", pdf: "ONLYOFFICE PDF",
};

function Dashboard({ summary: s, apps, art, desk, rels, offlineCount, onPick, onOpen }: {
  summary: Summary; apps: AppDef[]; art: ArtState | null; desk: boolean; rels: { kind: string; version: string; ready: boolean; size: number }[];
  offlineCount: number | null; onPick: (id: string) => void; onOpen: (id: string) => void;
}) {
  const docsApk = rels.find(r => r.kind === "docs_apk" && r.ready);
  const boxApk = rels.find(r => r.kind === "apk" && r.ready);
  const inApp = isInsideTimboxApp();
  const max = Math.max(1, ...apps.map(a => s.by_app[a.id]?.count ?? 0));
  const kpi = (label: string, value: string, sub?: string) => (
    <div className="t-card" style={{ padding: "12px 14px", display: "block" }}>
      <div style={{ fontSize: 12, color: "var(--text-3)" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
  return (
    <div data-testid="dashboard" style={{ display: "grid", gap: 12, marginBottom: 16 }}>
      {docsApk && (
        <div className="t-card" data-testid="app-download" style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 12, padding: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "#6E6FCF", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><I.Docs size={24}/></div>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>팀_문서 앱 {inApp ? "— 최신 버전" : "받기"}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-2)" }}>
              안드로이드 v{docsApk.version} · {fmtBytes(docsApk.size)} · 다른 앱에서 'TIM_BOX 로 열기', 오프라인 보기, 자동 업데이트
              {offlineCount != null && ` · 이 휴대폰에 보관 ${offlineCount}개`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a className="t-btn t-btn-primary t-btn-sm" data-action="get-docs-apk" href="/api/v1/app/download/docs_apk" style={{ background: "#6E6FCF", borderColor: "#6E6FCF", whiteSpace: "nowrap" }}>
              <I.Download size={14}/> 안드로이드 앱 받기
            </a>
            {boxApk && <a className="t-btn t-btn-sm" href="/api/v1/app/download/apk" style={{ whiteSpace: "nowrap" }}>TIM_BOX 전체 앱 (v{boxApk.version})</a>}
            <a className="t-btn t-btn-sm t-btn-ghost" href={APP_INSTALL_URL} target="_blank" rel="noreferrer" style={{ whiteSpace: "nowrap" }}>PC·다른 기기</a>
          </div>
        </div>
      )}
      <div className="ds-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {kpi("내 문서", `${s.total_files}개`, `서버 저장 ${fmtBytes(s.total_size)}`)}
        {kpi("편집한 문서", `${s.edited_files}개`, `오늘 ${s.edited_today}개`)}
        {kpi("버전 기록", `${s.versions.count}판`, `되돌리기용 ${fmtBytes(s.versions.size)}`)}
        {kpi("마지막 작업", s.last_activity ? fmtTime(s.last_activity).slice(5) : "-", "모든 기기에서 같은 목록")}
        <div className="ds-hide-m" style={{ display: "contents" }}>{kpi("ArtCraft", art?.version ? `v${art.version}` : "-", art?.last_checked ? `확인 ${fmtTime(art.last_checked).slice(5)}` : "")}</div>
      </div>
      <div className="ds-two" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12 }}>
        <div className="t-card ds-hide-m" style={{ padding: 14, display: "block" }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>형식별 문서 · 웹 편집기</div>
          <div style={{ display: "grid", gap: 7 }}>
            {apps.map(a => {
              const b = s.by_app[a.id] ?? { count: 0, size: 0 };
              return (
                <button key={a.id} onClick={() => onPick(a.id)} data-dash-app={a.id}
                  style={{ display: "grid", gridTemplateColumns: "26px 72px 1fr 44px", alignItems: "center", gap: 8, border: 0, background: "transparent", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--text)" }}>
                  <AppBadge id={a.id} color={a.color} size={24}/>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{a.label}</span>
                  <span title={EDITOR_OF[a.id]} style={{ position: "relative", height: 18, background: "var(--bg-1)", borderRadius: 4, overflow: "hidden" }}>
                    <span style={{ position: "absolute", inset: 0, width: `${(b.count / max) * 100}%`, background: a.color, opacity: .85, borderRadius: 4 }}/>
                    <span style={{ position: "absolute", left: 6, top: 1, fontSize: 11, color: b.count ? "#fff" : "var(--text-3)", whiteSpace: "nowrap" }}>{EDITOR_OF[a.id]}</span>
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, textAlign: "right" }}>{b.count}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="t-card" style={{ padding: 14, display: "block" }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>최근 작업한 문서</div>
          {s.recent.length === 0
            ? <div style={{ fontSize: 13, color: "var(--text-3)" }}>아직 문서가 없습니다.</div>
            : s.recent.map(f => {
              const a = apps.find(x => x.id === f.app);
              return (
                <button key={f.id} onClick={() => onOpen(f.id)} data-recent={f.name}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", border: 0, background: "transparent", padding: "5px 0", cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--text)" }}>
                  <AppBadge id={f.app} color={a?.color ?? "#94A3B8"} size={22}/>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                  {f.rev > 1 && <span style={{ fontSize: 11, color: "var(--p-600)" }}>{f.rev}판</span>}
                  <span style={{ fontSize: 11.5, color: "var(--text-3)", whiteSpace: "nowrap" }}>{fmtTime(f.updated_at).slice(5)}</span>
                </button>
              );
            })}
          <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 8 }}>
            서버의 내 폴더({s.storage})에 저장됩니다 · 웹·휴대폰 앱{desk ? "·이 PC 앱" : "·데스크톱 앱"}에서 같은 목록
          </div>
        </div>
      </div>
    </div>
  );
}

function ArtCraftPanel(p: {
  art: ArtState | null; artLocal: ArtLocal | null; desk: boolean; busy: boolean; autoUpdate: boolean; needsUpdate: boolean;
  onSync: () => void; onDownload: (a: Asset) => void; onInstall: () => void; onLaunch: () => void; onToggleAuto: () => void;
}) {
  const { art, artLocal } = p;
  const assets = [...(art?.assets ?? [])].sort((a, b) =>
    Number(b.os === userOS) - Number(a.os === userOS) || Number(b.primary) - Number(a.primary));
  return (
    <div data-testid="artcraft-panel" style={{ display: "grid", gap: 14 }}>
      <div className="t-card" style={{ padding: 18, display: "flex", flexDirection: "row", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <AppBadge id="artcraft" color={ART_COLOR} size={52}/>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>ArtCraft {art?.version ? `v${art.version}` : ""}</div>
          <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>
            AI 이미지·영상 제작 데스크톱 앱. 서버가 {art?.sync_hours ?? 6}시간마다 새 버전을 확인해 자동으로 받아 둡니다.
          </div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <span>배포일 {fmtTime(art?.published_at)}</span>
            <span>마지막 확인 {fmtTime(art?.last_checked)}</span>
            {art?.syncing && <span style={{ color: "var(--info)" }}>새 버전 받는 중…</span>}
            {art?.last_error && <span style={{ color: "var(--danger)" }}>오류: {art.last_error}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="t-btn t-btn-sm" onClick={p.onSync} disabled={p.busy || art?.syncing}><I.Refresh size={14}/> 지금 업데이트 확인</button>
          {art?.release_url && <a className="t-btn t-btn-sm t-btn-ghost" href={art.release_url} target="_blank" rel="noreferrer">릴리스 노트</a>}
        </div>
      </div>

      {p.desk && (
        <div className="t-card" style={{ padding: 16, display: "flex", flexDirection: "row", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 700 }}>이 PC</div>
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>
              {artLocal?.installed ? `ArtCraft ${artLocal.version ?? ""} 설치됨` : "ArtCraft가 설치돼 있지 않습니다"}
              {p.needsUpdate && <b style={{ color: "var(--warn)", marginLeft: 6 }}>→ v{art?.version} 업데이트 가능</b>}
            </div>
          </div>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={p.autoUpdate} onChange={p.onToggleAuto}/> 새 버전 자동 설치
          </label>
          {artLocal?.installed && <button className="t-btn t-btn-sm" onClick={p.onLaunch}>실행</button>}
          {(!artLocal?.installed || p.needsUpdate) && (
            <button className="t-btn t-btn-primary t-btn-sm" onClick={p.onInstall} disabled={p.busy} style={{ background: ART_COLOR, borderColor: ART_COLOR }}>
              {p.busy ? "설치 중…" : artLocal?.installed ? "업데이트" : "설치"}
            </button>
          )}
        </div>
      )}

      <div className="t-card" style={{ padding: 0, overflow: "hidden", display: "block" }}>
        <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", fontWeight: 700 }}>설치 파일 내려받기</div>
        {assets.length === 0 && <div style={{ padding: 20, color: "var(--text-3)", fontSize: 13 }}>아직 받아 둔 설치 파일이 없습니다. '지금 업데이트 확인' 을 눌러 주세요.</div>}
        {assets.map(a => {
          const prog = art?.progress?.[a.name];
          return (
            <div key={a.name} data-asset={a.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ width: 70, fontSize: 12, fontWeight: 700, color: a.os === userOS ? ART_COLOR : "var(--text-3)" }}>
                {a.os === "windows" ? "Windows" : a.os === "mac" ? "macOS" : "기타"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.label}{a.os === userOS && a.primary && <span style={{ marginLeft: 6, fontSize: 11, color: ART_COLOR }}>이 기기 추천</span>}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{a.name} · {fmtBytes(a.size)}{a.hint ? ` · ${a.hint}` : ""}</div>
              </div>
              {a.ready
                ? <button className="t-btn t-btn-sm" onClick={() => p.onDownload(a)}><I.Download size={14}/> 내려받기</button>
                : <span style={{ fontSize: 12, color: "var(--text-3)" }}>{prog ? `받는 중 ${Math.round(prog.done / Math.max(prog.total, 1) * 100)}%` : "준비 중"}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
